import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogComponent } from '@app/components';
import { HttpError } from '@app/errors/http/http.error';
import { NotifyService } from '@app/services';
import { ChangeRequestTemplateFieldOptions } from '@app/types/translatables/change-request-template-display.options';
import { FieldValueSetInterface } from '@change-requests/interfaces';
import { EmployeeFieldValueService } from '@change-requests/services';
import { StoreView } from '@common/views';
import { ChangeRequestFieldValue } from '@models/change-requests/change-request-field-value.model';
import { ChangeRequestTemplateField } from '@models/change-requests/change-request-template-field.model';
import { ChangeRequestTemplate } from '@models/change-requests/change-request-template.model';
import { ChangeRequest } from '@models/change-requests/change-request.model';
import { Employee } from '@models/employee/employee.model';

@Component({
    templateUrl: './change-request-store.template.html',
    styleUrls: ['./change-request-store.style.scss'],
})
export class ChangeRequestStoreView extends StoreView implements OnInit {
    @ViewChild('confirmRequestSubmission') confirmRequestSubmission: ConfirmationDialogComponent;
    title = '';
    changeRequestTemplate: ChangeRequestTemplate;
    changeRequest: ChangeRequest = new ChangeRequest();
    employee: Employee;
    isLoading = true;
    employeeValues: FieldValueSetInterface = {};

    isConfirming = false;

    effectiveDate: string;
    updatedFieldsMap: Map<string, boolean> = new Map();
    templateFieldsByKey: Map<string, ChangeRequestTemplateField> = new Map();
    changedFieldValues: Map<string, ChangeRequestFieldValue> = new Map();
    fieldErrorMap: Map<string, string> = new Map();

    changeRequestTemplateFieldOptions = ChangeRequestTemplateFieldOptions;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private notify: NotifyService,
        private employeeFieldService: EmployeeFieldValueService
    ) {
        super();

        this.changeRequestTemplate = this.route.snapshot.data?.models?.get('changeRequestTemplate');
        this.employee = this.route.snapshot.data?.models?.get('employee');
        this.title = `${this.changeRequestTemplate.name} - ${this.employee.fullName}`;

        for (const templateField of this.changeRequestTemplate.changeRequestTemplateFields) {
            this.templateFieldsByKey.set(templateField.key, templateField);
        }

        this.changeRequest.changeRequestTemplateId = this.changeRequestTemplate.id;
        this.changeRequest.subjectId = this.employee.id;

        const defaultEffectiveDate = new Date();
        defaultEffectiveDate.setHours(0, 0, 0, 0);

        this.effectiveDate = defaultEffectiveDate.toISOString();
    }

    async ngOnInit(): Promise<void> {
        this.employeeValues = await this.employeeFieldService.getFieldValues(this.changeRequestTemplate, this.employee);
        this.isLoading = false;
    }

    async handleUpdateCheckboxChange(key: string, isChecked: boolean): Promise<void> {
        if (isChecked === true) {
            this.updatedFieldsMap.set(key, isChecked);
        } else {
            this.updatedFieldsMap.delete(key);
            this.changedFieldValues.delete(key);
        }
    }

    async handleFieldValueChange(key: string, value: any): Promise<void> {
        // If the field's new value is the same as its initial value,
        // remove it from the map of changed fields.
        if (value === this.employeeValues[key]) {
            this.changedFieldValues.delete(key);

            return;
        }

        // Replace undefined values with null to allow them to be serialized by the model.
        if (value === undefined) {
            value = null;
        }

        const templateField = this.templateFieldsByKey.get(key);

        const newFieldValue = new ChangeRequestFieldValue({
            humiChangeRequestFieldId: templateField.id,
            oldValue: this.employeeValues[key],
            newValue: value,
            isUpdated: true,
        });

        this.changedFieldValues.set(key, newFieldValue);
    }

    applyChanges(): void {
        const preparedFieldValues = [];

        for (const fieldValue of this.changedFieldValues.values()) {
            preparedFieldValues.push(fieldValue.getAttributes());
        }

        this.changeRequest.effectiveAt = new Date(this.effectiveDate);
        this.changeRequest.fieldValues = preparedFieldValues;
    }

    async onSave(): Promise<void> {
        // The isConfirming flag is used to conditionally render the display components on the confirmation dialog.
        // Without this flag, the display components will re-render and make API requests, even when the dialog is closed.
        this.isConfirming = true;

        const confirmed = await this.confirmRequestSubmission.open();

        this.isConfirming = false;

        if (confirmed) {
            try {
                this.applyChanges();

                await this.changeRequest.save();

                this.notify.success('change-requests.requestSubmitted');

                await this.router.navigate(['change-requests', 'requests', this.changeRequest.id]);
            } catch (e) {
                const { res: response } = e as HttpError;
                if (response?.status === 400) {
                    this.mapValidationErrors(response?.error.errors);
                } else {
                    throw e;
                }
            }

            return;
        }

        this.notify.info('change-requests.requestCancelled');
    }

    mapValidationErrors(errors: { detail: string; source: { pointer: string } }[]): void {
        for (const error of errors) {
            const fieldKey = error.source.pointer.replaceAll('data.attributes.', '');
            const message = error.detail.replaceAll('data.attributes.', '');

            this.fieldErrorMap.set(fieldKey, message);
        }
    }
}
